import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { runDeployValidation, deployValidationHistory, lastDeployValidation } from "@/lib/api";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, ShieldWarning, Play, CheckCircle, Warning, XCircle, ClockCounterClockwise, GitCommit, Timer,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DOT = {
  pass: "bg-emerald-400", warning: "bg-amber-400", fail: "bg-red-500",
};
const ICON = {
  pass: <CheckCircle size={16} className="text-emerald-400" weight="fill" />,
  warning: <Warning size={16} className="text-amber-400" weight="fill" />,
  fail: <XCircle size={16} className="text-red-400" weight="fill" />,
};
const fmtAt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" });
const fmtDur = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

function Verdict({ run }) {
  if (!run) return null;
  const safe = run.safe_to_deploy;
  return (
    <div data-testid="deploy-verdict" className={`rounded-xl border p-5 mb-6 flex items-center gap-4 ${safe ? "bg-emerald-500/[0.08] border-emerald-500/25" : "bg-red-500/[0.07] border-red-500/25"}`}>
      {safe ? <ShieldCheck size={40} className="text-emerald-400" weight="fill" /> : <ShieldWarning size={40} className="text-red-400" weight="fill" />}
      <div className="flex-1">
        <div className={`font-head text-2xl font-semibold ${safe ? "text-emerald-300" : "text-red-300"}`}>
          {safe ? "SAFE TO DEPLOY" : "NOT SAFE TO DEPLOY"}
        </div>
        <div className="text-xs text-[#9a9a9a] mt-1">
          {safe ? "Every required production test passed with no failures."
            : "One or more required tests did not pass — resolve them before deploying."}
        </div>
      </div>
      <div className="text-right text-[11px] text-[#9a9a9a] space-y-1 shrink-0">
        <div data-testid="deploy-run-at" className="flex items-center gap-1.5 justify-end"><ClockCounterClockwise size={13} /> {fmtAt(run.at)}</div>
        <div data-testid="deploy-build-version" className="flex items-center gap-1.5 justify-end"><GitCommit size={13} /> Build <span className="font-mono text-[#cfcfcf]">{run.build_version}</span></div>
        <div data-testid="deploy-duration" className="flex items-center gap-1.5 justify-end"><Timer size={13} /> {fmtDur(run.duration_ms)}</div>
      </div>
    </div>
  );
}

export default function DeploymentValidationPage() {
  const qc = useQueryClient();
  const [run, setRun] = useState(null);
  const { data: last } = useQuery({ queryKey: ["deploy-validation-last"], queryFn: lastDeployValidation });
  const { data: hist } = useQuery({ queryKey: ["deploy-validation-history"], queryFn: deployValidationHistory });

  const m = useMutation({
    mutationFn: runDeployValidation,
    onSuccess: (data) => {
      setRun(data);
      qc.invalidateQueries({ queryKey: ["deploy-validation-history"] });
      qc.invalidateQueries({ queryKey: ["deploy-validation-last"] });
      toast[data.safe_to_deploy ? "success" : "error"](data.safe_to_deploy ? "All required tests passed — safe to deploy" : "Validation found blocking issues");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Validation run failed"),
  });

  const current = run || last?.run;
  const results = current?.results || [];
  const counts = current?.counts || {};

  return (
    <PageContainer>
      <PageHeader eyebrow="Internal · Super Admin only" title="Deployment Validation"
        subtitle="One-click production readiness suite. Probes every core system and integration — read-only, nothing is published or charged.">
        <Button data-testid="run-validation-btn" onClick={() => m.mutate()} disabled={m.isPending}
          className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
          <Play size={16} weight="fill" className={`mr-1.5 ${m.isPending ? "ai-thinking" : ""}`} />
          {m.isPending ? "Running full validation…" : "Run Full Validation"}
        </Button>
      </PageHeader>

      {!current && !m.isPending && (
        <div className="card-matte p-8 text-center text-[#9a9a9a]" data-testid="deploy-empty">
          No validation runs yet — click <b className="text-white">Run Full Validation</b> to test every production system.
        </div>
      )}
      {m.isPending && (
        <div className="card-matte p-8 text-center text-[#9a9a9a] ai-thinking" data-testid="deploy-running">
          Running {" "}17 production checks — database, auth, payments, AI, publishing paths…
        </div>
      )}

      {current && !m.isPending && (
        <>
          <Verdict run={current} />
          <div className="flex items-center gap-3 mb-4 text-xs" data-testid="deploy-counts">
            <span className="flex items-center gap-1.5 text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" /> {counts.pass || 0} passed</span>
            <span className="flex items-center gap-1.5 text-amber-400"><span className="h-2 w-2 rounded-full bg-amber-400" /> {counts.warning || 0} warnings</span>
            <span className="flex items-center gap-1.5 text-red-400"><span className="h-2 w-2 rounded-full bg-red-500" /> {counts.fail || 0} failed</span>
          </div>
          <div className="space-y-2 mb-8" data-testid="deploy-results">
            {results.map((r) => (
              <div key={r.key} data-testid={`deploy-test-${r.key}`}
                className="card-matte px-4 py-3 flex items-start gap-3">
                <span className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${DOT[r.status]}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{r.name}</span>
                    {r.required && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white/8 text-[#9a9a9a]">REQUIRED</span>}
                  </div>
                  <div className="text-xs text-[#9a9a9a] mt-0.5">{r.detail}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-[#7a7a7a]">{fmtDur(r.duration_ms || 0)}</span>
                  {ICON[r.status]}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="label-caps mb-3">Deployment validation history</div>
      <div className="card-matte overflow-hidden" data-testid="deploy-history">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[#7a7a7a] border-b border-white/8">
              <th className="px-4 py-2.5 font-medium">When</th>
              <th className="px-4 py-2.5 font-medium">Build</th>
              <th className="px-4 py-2.5 font-medium">Duration</th>
              <th className="px-4 py-2.5 font-medium">Pass / Warn / Fail</th>
              <th className="px-4 py-2.5 font-medium">Run by</th>
              <th className="px-4 py-2.5 font-medium text-right">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {(hist?.runs || []).map((h) => (
              <tr key={h.id} className="border-b border-white/5 last:border-0 text-[#cfcfcf]">
                <td className="px-4 py-2.5">{fmtAt(h.at)}</td>
                <td className="px-4 py-2.5 font-mono">{h.build_version}</td>
                <td className="px-4 py-2.5">{fmtDur(h.duration_ms)}</td>
                <td className="px-4 py-2.5">
                  <span className="text-emerald-400">{h.counts?.pass || 0}</span> / <span className="text-amber-400">{h.counts?.warning || 0}</span> / <span className="text-red-400">{h.counts?.fail || 0}</span>
                </td>
                <td className="px-4 py-2.5">{h.run_by || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  {h.safe_to_deploy
                    ? <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">SAFE TO DEPLOY</span>
                    : <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-500/15 text-red-400">NOT SAFE</span>}
                </td>
              </tr>
            ))}
            {!(hist?.runs || []).length && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#7a7a7a]">No runs recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
}
